// Runtime Supabase config primitives (Phase 3b).
//
// Deployed admin/portal SPAs boot from GET /api/config instead of build-time
// constants, so one bundle serves shared-mode tenants (the shared project) and
// dedicated tenants (their own Supabase cell). `ref` is the cell's project ref
// and namespaces the auth storage keys PER PROJECT:
//
//   ref: null  → the shared project → the legacy un-suffixed keys
//                ('l2u-company-auth', 'l2u-admin-auth'), so shipping this
//                phase signs nobody out.
//   ref: 'abc' → that cell → 'l2u-company-auth.abc' etc.
//
// A token minted by one auth pool is meaningless to every other, and under the
// hybrid a tenant can MIGRATE shared→dedicated while users hold live sessions.
// Because the key changes with the project, the SPA simply finds no session
// under the new key and lands on a clean login — never an error loop — and
// purgeStaleAuthKeys removes the orphaned stale-pool entry at boot.

// Which hostname layout the API is running. Mirrors HostConfig['mode'] in
// apps/api/src/lib/hostConfig.ts; duplicated rather than imported because
// web-shared must not depend on the API package.
export type HostMode = 'audiences' | 'legacy' | 'off';

// Which audience base the SPA's own origin sits under, as resolved by the API
// (apps/api/src/lib/parseHost.ts). Mirrored rather than imported for the same
// reason HostMode is: web-shared must not depend on the API package.
export type Audience = 'admin' | 'company' | 'driver' | 'visitor';
const AUDIENCES: readonly Audience[] = ['admin', 'company', 'driver', 'visitor'];

export type RuntimeSupabaseConfig = {
  url: string;
  publishableKey: string;
  ref: string | null;
  hostMode: HostMode;
  /**
   * The audience this origin belongs to, or null when the host names none.
   *
   * The portal serves /c/*, /d/* and /visit* from ONE bundle on three
   * hostnames and uses this to refuse the families that are not its own.
   * Null on the legacy shared base (which answers all three), on a platform
   * host, in local dev, and on any host under no configured base.
   */
  audience: Audience | null;
  /**
   * This tenant's company and driver sign-in pages, for the portal's
   * cross-audience link ("Are you a driver?"). On a per-audience host that
   * link changes HOSTNAME, which the SPA cannot build: the base domains live
   * in the Worker. Null wherever both families share this origin and a
   * relative link works.
   */
  signInUrls: { company: string; driver: string } | null;
};

// Only an absolute https URL is accepted, because these become hrefs: a
// `javascript:` value from a compromised or misconfigured API must not.
function httpsUrl(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  try {
    return new URL(value).protocol === 'https:' ? value : null;
  } catch {
    return null;
  }
}

function parseSignInUrls(value: unknown): RuntimeSupabaseConfig['signInUrls'] {
  if (typeof value !== 'object' || value === null) return null;
  const rec = value as Record<string, unknown>;
  const company = httpsUrl(rec.company);
  const driver = httpsUrl(rec.driver);
  return company && driver ? { company, driver } : null;
}

export function parseConfigResponse(body: unknown): RuntimeSupabaseConfig {
  const rec = typeof body === 'object' && body !== null ? (body as Record<string, unknown>) : {};
  const url = typeof rec.url === 'string' ? rec.url : '';
  const publishableKey = typeof rec.publishableKey === 'string' ? rec.publishableKey : '';
  const ref = typeof rec.ref === 'string' && rec.ref !== '' ? rec.ref : null;
  // An ABSENT or unrecognised hostMode defaults to 'legacy', and the asymmetry
  // is deliberate. Guessing 'legacy' on an audiences-mode environment keeps the
  // `/admin` prefix on a host that owns its whole path space: uglier URLs, but
  // every one of them resolves. Guessing 'audiences' on a legacy environment
  // drops the prefix that Cloudflare path-splits on, and the SPA emits links
  // another Worker serves. Only one of those two mistakes is recoverable by
  // the user pressing back.
  const hostMode: HostMode =
    rec.hostMode === 'audiences' || rec.hostMode === 'off' ? rec.hostMode : 'legacy';
  // FAILS OPEN, and the asymmetry with hostMode above is deliberate in the
  // other direction. A wrong audience 404s every page on the host; a null one
  // only restores the behaviour that shipped before the gate existed. So an
  // older API that sends no `audience`, or anything unrecognised, leaves the
  // gate open rather than blacking out a portal.
  const audience: Audience | null = AUDIENCES.includes(rec.audience as Audience)
    ? (rec.audience as Audience)
    : null;
  if (!url || !publishableKey) {
    throw new Error('Malformed /api/config response: url and publishableKey are required');
  }
  const signInUrls = parseSignInUrls(rec.signInUrls);
  return { url, publishableKey, ref, hostMode, audience, signInUrls };
}

/**
 * The React Router basename for the admin SPA.
 *
 * In `audiences` mode each audience owns a hostname, so the admin SPA is
 * mounted at the root and every path it emits is prefix-free. In `legacy` (and
 * `off`) mode the `/admin` prefix is load-bearing beyond cosmetics: on a shared
 * wildcard host it is how Cloudflare decides between the admin Worker and the
 * portal one, so the router re-adds it to every link and strips it from every
 * matched location. Route tables and nav entries stay root-relative either way.
 */
export function adminBasename(mode: HostMode): string {
  return mode === 'audiences' ? '/' : '/admin';
}

export type AuthStorageKeyBase = 'l2u-company-auth' | 'l2u-admin-auth';

export function storageKeyFor(base: AuthStorageKeyBase, ref: string | null): string {
  return ref ? `${base}.${ref}` : base;
}

// Only the surface the purge touches, so tests use a plain fake. The ops key
// ('l2u-ops-auth') is deliberately NOT a family below: the ops console never
// boots from /api/config and its key is never namespaced.
export type StorageLike = {
  length: number;
  key(index: number): string | null;
  removeItem(key: string): void;
};

const FAMILIES: readonly AuthStorageKeyBase[] = ['l2u-company-auth', 'l2u-admin-auth'];

export function purgeStaleAuthKeys(storage: StorageLike, ref: string | null): void {
  const stale: string[] = [];
  for (let i = 0; i < storage.length; i++) {
    const key = storage.key(i);
    if (!key) continue;
    for (const family of FAMILIES) {
      // A key belongs to this family bare, ref-suffixed ('.') or with a
      // supabase-js auxiliary suffix ('-', e.g. -code-verifier).
      if (key !== family && !key.startsWith(`${family}.`) && !key.startsWith(`${family}-`)) {
        continue;
      }
      const current = storageKeyFor(family, ref);
      if (key !== current && !key.startsWith(`${current}-`)) stale.push(key);
    }
  }
  for (const key of stale) storage.removeItem(key);
}
